import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { useTranslation } from '../i18n';
import {
  ClientIntakeFormData,
  FormErrors,
  initialFormData,
} from '../types/ClientIntakeForm';
import {
  buildClientIntakeEmailBody,
  buildClientIntakeEmailSubject,
  buildClientIntakeMailtoLink,
  clearFormDraft,
  generateFormSummary,
  loadFormDraft,
  saveFormDraft,
  validateForm,
} from '../utils/formUtils';

const DESIGN_TEAM_EMAIL = 'delythug@gmail.com';

interface PreparedEmailDraft {
  mailtoLink: string;
  subject: string;
  body: string;
  summary: string;
}

const ClientIntake: React.FC = () => {
  const { locale, raw, t } = useTranslation();
  const [formData, setFormData] = useState<ClientIntakeFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedDraft, setHasLoadedDraft] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [preparedEmailDraft, setPreparedEmailDraft] = useState<PreparedEmailDraft | null>(null);
  const silhouetteOptions = Object.entries(
    raw<Record<string, string>>('formOptions.dressSilhouettes')
  );
  const colorOptions = Object.entries(
    raw<Record<string, string>>('formOptions.preferredColors')
  );
  const necklineOptions = Object.entries(
    raw<Record<string, string>>('formOptions.neckline')
  );
  const budgetOptions = Object.entries(
    raw<Record<string, string>>('formOptions.budget')
  );
  const timelineOptions = Object.entries(
    raw<Record<string, string>>('formOptions.timeline')
  );
  const preferenceOptions = Object.entries(
    raw<Record<string, string>>('formOptions.preference')
  );
  const howDidYouHearOptions = Object.entries(
    raw<Record<string, string>>('formOptions.howDidYouHear')
  );
  const captureItems = raw<string[]>('clientIntake.captureItems');

  useEffect(() => {
    const savedDraft = loadFormDraft();

    if (savedDraft) {
      setFormData({
        ...initialFormData,
        ...savedDraft,
      });
    }

    setHasLoadedDraft(true);
  }, []);

  useEffect(() => {
    if (!hasLoadedDraft) {
      return;
    }

    saveFormDraft(formData);
  }, [formData, hasLoadedDraft]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleInputChange = (field: keyof ClientIntakeFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    setErrors(prev => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleCheckboxChange = (field: 'dressSilhouette' | 'preferredColors', value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleCopyEmailBrief = async () => {
    if (!preparedEmailDraft) {
      return;
    }

    try {
      await navigator.clipboard.writeText(preparedEmailDraft.body);
      setStatusMessage(t('clientIntake.status.clipboardCopied'));
    } catch (error) {
      console.error('Clipboard copy error:', error);
      setStatusMessage(t('clientIntake.status.clipboardUnavailable'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    const validationErrors = validateForm(formData, locale);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatusMessage(t('clientIntake.status.validationError'));
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = buildClientIntakeEmailSubject(formData, locale);
      const body = buildClientIntakeEmailBody(formData, locale);
      const summary = generateFormSummary(formData, locale);
      const mailtoLink = buildClientIntakeMailtoLink(
        formData,
        DESIGN_TEAM_EMAIL,
        locale
      );

      setPreparedEmailDraft({
        mailtoLink,
        subject,
        body,
        summary,
      });

      window.location.href = mailtoLink;
      clearFormDraft();
      setFormData(initialFormData);
      setErrors({});
      setStatusMessage(t('clientIntake.status.success'));
    } catch (error) {
      console.error('Form submission error:', error);
      setStatusMessage(t('clientIntake.status.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3ece3]">
      <Seo
        pageTitle={t('seo.designTitle')}
        description={t('seo.designDescription')}
      />
      <Navigation scrollToSection={scrollToSection} />
      
      {/* Client Intake Section */}
      <section className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.7),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(214,189,159,0.14),transparent_28%)] pt-6 pb-16">
        <div className="container mx-auto px-4 py-10">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-white/70 bg-[rgba(255,250,244,0.72)] p-4 shadow-[0_28px_90px_rgba(117,95,74,0.12)] backdrop-blur-xl sm:p-6">
            <div className="mx-auto max-w-4xl">
              <h1 className="mb-6 text-center text-4xl font-light tracking-[0.14em] text-stone-800">
                {t('clientIntake.title')}
              </h1>
              <p className="mb-10 text-center text-lg leading-8 text-stone-700">
                {t('clientIntake.subtitle')}
              </p>

              <div className="mb-8 rounded-[1.75rem] border border-[#eadbc8] bg-[#fffdf9] p-6 shadow-[0_24px_60px_rgba(117,95,74,0.10)] sm:p-8">
                <div className="grid items-start gap-8 md:grid-cols-[1.4fr_1fr]">
                  <div>
                    <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-stone-600">
                      {t('clientIntake.eyebrow')}
                    </p>
                    <h2 className="mb-4 text-3xl font-light tracking-[0.06em] text-stone-900">
                      {t('clientIntake.heroTitle')}
                    </h2>
                    <p className="mb-6 text-stone-700">
                      {t('clientIntake.heroDescription')}
                    </p>
                    <div className="flex flex-col gap-4 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => scrollToSection('intake-form')}
                        className="inline-flex w-full items-center justify-center rounded-full bg-[#2f241d] px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a] sm:w-auto"
                      >
                        {t('clientIntake.designButton')}
                      </button>
                      <a
                        href={`mailto:${DESIGN_TEAM_EMAIL}`}
                        className="inline-flex w-full items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-8 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9] sm:w-auto"
                      >
                        {t('clientIntake.contactTeamButton')}
                      </a>
                    </div>
                  </div>

                  <div className="rounded-[1.35rem] border border-[#efe1d2] bg-[#fffaf4] p-6 shadow-[0_12px_35px_rgba(117,95,74,0.08)]">
                    <h3 className="mb-4 text-lg font-semibold text-stone-900">{t('clientIntake.captureTitle')}</h3>
                    <ul className="space-y-3 text-sm text-stone-700">
                      {captureItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {statusMessage && (
                <div
                  className={`rounded-xl px-5 py-4 mb-6 text-sm ${
                    preparedEmailDraft
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  aria-live="polite"
                >
                  {statusMessage}
                </div>
              )}

              {preparedEmailDraft && (
                <div className="mb-8 rounded-[1.5rem] border border-[#eadbc8] bg-[#fffdf9] p-6 shadow-[0_20px_50px_rgba(117,95,74,0.10)]">
                  <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2 className="mb-2 text-2xl font-semibold text-stone-900">{t('clientIntake.preparedTitle')}</h2>
                      <p className="text-stone-700">
                        {t('clientIntake.preparedDescription')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <a
                        href={preparedEmailDraft.mailtoLink}
                        className="inline-flex items-center justify-center rounded-full bg-[#2f241d] px-7 py-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300 hover:bg-[#43342a]"
                      >
                        {t('clientIntake.openDraftAgain')}
                      </a>
                      <button
                        type="button"
                        onClick={handleCopyEmailBrief}
                        className="inline-flex items-center justify-center rounded-full border border-[#d8c7b2] bg-[#fffaf4]/82 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-800 backdrop-blur-sm transition duration-300 hover:border-[#bea07a] hover:bg-[#fffdf9]"
                      >
                        {t('clientIntake.copyEmailBrief')}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="mb-2 text-sm font-semibold text-stone-700">{t('clientIntake.subject')}</p>
                      <div className="rounded-[1rem] border border-[#efe1d2] bg-[#fffaf4] px-4 py-3 text-sm text-stone-800">
                        {preparedEmailDraft.subject}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-semibold text-stone-700">{t('clientIntake.clientSummary')}</p>
                      <pre className="whitespace-pre-wrap rounded-[1rem] border border-[#efe1d2] bg-[#fffaf4] p-4 text-sm text-stone-800">
                        {preparedEmailDraft.summary}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
              
              <form id="intake-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('clientIntake.sections.personalInformation')}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.firstName')}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.firstName ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.firstName && <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.lastName')}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.lastName ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.lastName && <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.email ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full rounded-xl border bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a] ${
                        errors.phone ? 'border-red-400' : 'border-[#d8c7b2]'
                      }`}
                    />
                    {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Wedding Details */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('clientIntake.sections.weddingDetails')}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="weddingDate" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.weddingDate')}
                    </label>
                    <input
                      type="date"
                      id="weddingDate"
                      value={formData.weddingDate}
                      onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    />
                  </div>
                  <div>
                    <label htmlFor="venue" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.venue')}
                    </label>
                    <input
                      type="text"
                      id="venue"
                      value={formData.venue}
                      onChange={(e) => handleInputChange('venue', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    />
                  </div>
                  <div>
                    <label htmlFor="budget" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.budget')}
                    </label>
                    <select
                      id="budget"
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectBudget')}</option>
                      {budgetOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.timeline')}
                    </label>
                    <select
                      id="timeline"
                      value={formData.timeline}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectTimeline')}</option>
                      {timelineOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preference" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.preference')}
                    </label>
                    <select
                      id="preference"
                      value={formData.preference}
                      onChange={(e) => handleInputChange('preference', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectOption')}</option>
                      {preferenceOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Style Preferences */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('clientIntake.sections.stylePreferences')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.dressSilhouette')}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {silhouetteOptions.map(([value, label]) => (
                        <label key={value} className="flex items-center">
                          <input
                            type="checkbox"
                            value={value}
                            checked={formData.dressSilhouette.includes(value)}
                            onChange={(e) => handleCheckboxChange('dressSilhouette', value, e.target.checked)}
                            className="mr-2 text-[#2f241d] focus:ring-[#bea07a]"
                          />
                          <span className="text-sm text-stone-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.preferredColors')}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {colorOptions.map(([value, label]) => (
                        <label key={value} className="flex items-center">
                          <input
                            type="checkbox"
                            value={value}
                            checked={formData.preferredColors.includes(value)}
                            onChange={(e) => handleCheckboxChange('preferredColors', value, e.target.checked)}
                            className="mr-2 text-[#2f241d] focus:ring-[#bea07a]"
                          />
                          <span className="text-sm text-stone-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="neckline" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.neckline')}
                    </label>
                    <select
                      id="neckline"
                      value={formData.neckline}
                      onChange={(e) => handleInputChange('neckline', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectNeckline')}</option>
                      {necklineOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="overflow-hidden rounded-[1.35rem] border border-[#eadbc8] bg-white p-6 shadow-[0_16px_45px_rgba(117,95,74,0.08)]">
                <h2 className="mb-5 border-b border-[#efe1d2] pb-4 text-2xl font-semibold text-stone-800">{t('clientIntake.sections.additionalInformation')}</h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="size" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.size')}
                    </label>
                    <select
                      id="size"
                      value={formData.size}
                      onChange={(e) => handleInputChange('size', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectSize')}</option>
                      {Array.from({length: 20}, (_, i) => i + 0).map(size => (
                        <option key={size} value={size.toString()}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="specialRequests" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.specialRequests')}
                    </label>
                    <textarea
                      id="specialRequests"
                      rows={4}
                      value={formData.specialRequests}
                      onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 placeholder:text-stone-400 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                      placeholder={t('clientIntake.placeholders.specialRequests')}
                    ></textarea>
                  </div>
                  <div>
                    <label htmlFor="howDidYouHear" className="mb-2 block text-sm font-medium text-stone-700">
                      {t('clientIntake.fields.howDidYouHear')}
                    </label>
                    <select
                      id="howDidYouHear"
                      value={formData.howDidYouHear}
                      onChange={(e) => handleInputChange('howDidYouHear', e.target.value)}
                      className="w-full rounded-xl border border-[#d8c7b2] bg-white/60 px-4 py-2 text-stone-800 shadow-sm outline-none backdrop-blur-sm transition focus:ring-2 focus:ring-[#bea07a]"
                    >
                      <option value="">{t('clientIntake.placeholders.selectOption')}</option>
                      {howDidYouHearOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${
                    isSubmitting 
                      ? 'cursor-not-allowed bg-stone-400' 
                      : 'bg-[#2f241d] hover:bg-[#43342a]'
                  } inline-flex items-center justify-center rounded-full px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition duration-300`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.submitting')}
                    </span>
                  ) : (
                    t('clientIntake.submitButton')
                  )}
                </button>
                <p className="mt-4 text-sm text-stone-600">
                  {t('clientIntake.submitHelper')}
                </p>
              </div>
            </form>
          </div>
        </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClientIntake;
